import { useState } from 'react';

import {
	Avatar,
	AppBar,
	Box,
	IconButton,
	Menu,
	Toolbar,
	Typography,
	Stack,
	useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ActionButton, ActionButtonAlignment } from '~components';
import ActionButtonStyle from '~components/buttons/actionButtonStyle';
import VectorImage from '~components/media/vectorImage';
import { pagePathSegments } from '~constants';
import pagePaths from '~constants/pagePaths';
import { StatusVariant } from '~enums';
import { getUserFullName, getUserInitials } from '~features/users';
import { useSession } from '~hooks';
import Dimens from '~themes/dimens';

interface Props {
	menuOpen?: boolean;
	onMenuToggleClick?: (open: boolean) => void;
}

interface HeaderOption {
	key: string;
	path: string;
	icon: React.ReactElement;
	label: string;
	variant?: StatusVariant;
}

const MainHeader = ({ menuOpen = true, onMenuToggleClick }: Props) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const options: HeaderOption[] = [
		{
			key: 'profile',
			path: pagePathSegments.Profile,
			icon: <VectorImage name='icon/user' />,
			label: t('profile', { ns: 'nav' }),
		},
		{
			key: 'logout',
			path: pagePaths.AuthLogout,
			icon: <VectorImage name='icon/logout' />,
			label: t('logout', { ns: 'auth' }),
			variant: StatusVariant.Destructive,
		},
	];
	const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

	const { user } = useSession();

	const fullName = user ? getUserFullName(user) : t('unknown', { ns: 'status' });
	const initials = user ? getUserInitials(user) : t('unknown', { ns: 'status' });

	const { palette } = useTheme();

	const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorElUser(event.currentTarget);
	};

	const handleCloseUserMenu = () => {
		setAnchorElUser(null);
	};

	const avatar = (
		<Avatar
			sx={{
				bgcolor: palette.bgQuaternary.main,
				width: Dimens.spacing5xl,
				height: Dimens.spacing5xl,
				color: palette.text.primary,
			}}
			alt={fullName}
		>
			<Typography variant='headingXs' component='span' sx={{ lineHeight: Dimens.spacingNone }}>
				{initials}
			</Typography>
		</Avatar>
	);

	return (
		<AppBar
			position='static'
			color='inherit'
			sx={{
				borderBottom: 1,
				borderColor: palette.borderSecondary.main,
				boxShadow: 'none',
			}}
		>
			<Toolbar
				sx={(theme) => ({
					height: Dimens.fixed(60),
					minHeight: Dimens.fixed(60),
					paddingLeft: Dimens.spacingMd,
					// Override MUI's default Toolbar min-height/padding bump at this breakpoint.
					[theme.breakpoints.up('sm')]: {
						paddingLeft: Dimens.spacingMd,
						minHeight: Dimens.fixed(60),
						justifyContent: 'space-between',
					},
					display: 'flex',
					justifyContent: {
						xs: 'space-between',
						lg: 'end',
					},
					paddingRight: {
						xs: Dimens.spacingMd,
						md: Dimens.spacing4xl,
					},
				})}
			>
				<IconButton
					size='large'
					aria-label='menu'
					aria-controls='menu-appbar'
					aria-haspopup='true'
					color='inherit'
					onClick={() => onMenuToggleClick?.(menuOpen)}
					sx={{ display: { xs: 'flex', lg: 'none' } }}
				>
					<VectorImage
						name='icon/side-panel'
						color={menuOpen ? palette.textDisabled.main : palette.textPrimary.main}
						size={Dimens.spacing2xl}
					/>
				</IconButton>
				<Stack
					spacing={1}
					direction='row'
					sx={{
						alignItems: 'center',
					}}
				>
					{/* {import.meta.env.VITE_DEV_MOCK_API && false && (
						<Tooltip content='sandbox'>
							<IconButton component={Link} to={pagePathSegments.Sandbox} color='inherit'>
								<ScienceIcon />
							</IconButton>
						</Tooltip>
					)} */}
					<IconButton sx={{ p: Dimens.spacingNone }} onClick={handleOpenUserMenu}>
						{avatar}
					</IconButton>
					<Menu
						sx={{ mt: Dimens.fixed(45) }}
						anchorEl={anchorElUser}
						anchorOrigin={{
							vertical: 'top',
							horizontal: 'right',
						}}
						keepMounted
						open={Boolean(anchorElUser)}
						onClose={handleCloseUserMenu}
					>
						<Box
							sx={{
								mb: Dimens.spacingSm,
								mt: Dimens.spacingSm,
							}}
						>
							{user != null && (
								<Stack
									spacing={1}
									direction='row'
									sx={{
										alignItems: 'center',
										verticalAlign: 'middle',
										mb: Dimens.spacingLg,
										mx: Dimens.spacing3xl,
									}}
								>
									{avatar}
									<Stack>
										<Typography variant='label' sx={{ color: palette.textPrimary.main }}>
											{fullName}
										</Typography>
										<Typography variant='paragraph' sx={{ color: palette.textQuaternary.main }}>
											{user.emailAddress}
										</Typography>
									</Stack>
								</Stack>
							)}
							<Box
								sx={{
									height: Dimens.spacing3xs,
									display: 'flex',
									alignItems: 'center',
									bgcolor: palette.borderTertiary.main,
									mx: Dimens.spacingXl,
									mb: Dimens.spacingLg,
								}}
							/>
							{options.map((option) => {
								return (
									<ActionButton
										sx={{ width: '100%' }}
										alignment={ActionButtonAlignment.Start}
										key={option.key}
										onClick={() => {
											handleCloseUserMenu();
											navigate(option.path);
										}}
										startIcon={option.icon}
										label={option.label}
										style={ActionButtonStyle.Text}
										variant={option.variant}
									/>
								);
							})}
						</Box>
					</Menu>
				</Stack>
			</Toolbar>
		</AppBar>
	);
};

export default MainHeader;
